/* A small custom dropdown for the Sales Ladder content tiles and toolbars: an
   icon, the current label and a chevron; the menu lists options with a one-line
   note and a check on the selected one. `warn` gives it the purple "needs
   setting" outline. Clicking outside or Esc closes it. */

import { useEffect, useState } from 'react';
import { Icon } from '../kit';
import { font } from '../style';
import { C } from '../tokens';

export interface PickOpt<T> { v: T; label: string; sub?: string; rm?: boolean }

export function Pick<T extends string | number>({ label, icon, value, options, onPick, warn, disabled, full, height = 28, menuWidth = 200, align = 'left', title }: {
  label: string;
  icon: string;
  value?: T;
  options: PickOpt<T>[];
  onPick: (v: T) => void;
  warn?: boolean;
  disabled?: boolean;
  full?: boolean;
  height?: number;
  menuWidth?: number;
  align?: 'left' | 'right';
  title?: string;
}) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [open]);
  return (
    <div style={{ position: 'relative', minWidth: 0, flex: full ? undefined : 'none' }}>
      <button
        type="button"
        title={title}
        disabled={disabled}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        style={{ width: full ? '100%' : undefined, height, boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: 5, padding: '0 6px 0 8px', background: C.input, border: `1px solid ${open ? C.purple : warn ? 'color-mix(in srgb, var(--om-accent) 60%, transparent)' : C.border}`, borderRadius: 6, color: warn ? C.purplePale : C.text, font: font(500, height < 28 ? 11.5 : 12), cursor: disabled ? 'default' : 'pointer', textAlign: 'left', opacity: disabled ? 0.5 : 1, whiteSpace: 'nowrap' }}
      >
        <Icon name={icon} size={12} color={C.sub} style={{ flex: 'none' }} />
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
        <Icon name="chevron-down" size={13} color={C.sub} style={{ flex: 'none' }} />
      </button>
      {open && (
        <>
          <div onClick={(e) => { e.stopPropagation(); setOpen(false); }} style={{ position: 'fixed', inset: 0, zIndex: 9 }} />
          <div className="cpt-thin-scroll" style={{ position: 'absolute', top: height + 4, [align]: 0, zIndex: 10, width: menuWidth, maxHeight: 280, overflow: 'auto', padding: 4, boxSizing: 'border-box', background: C.card, border: `1px solid ${C.borderHi}`, borderRadius: 8, boxShadow: '0 12px 32px rgba(0,0,0,.5)', display: 'flex', flexDirection: 'column', gap: 1 }}>
            {options.map((o) => {
              const on = value !== undefined && o.v === value;
              return (
                <button key={String(o.v)} type="button" className="cpt-hover-row" onClick={(e) => { e.stopPropagation(); setOpen(false); onPick(o.v); }} style={{ flex: 'none', display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 8px', border: 0, borderRadius: 6, background: on ? 'color-mix(in srgb, var(--om-accent) 12%, transparent)' : 'transparent', cursor: 'pointer', textAlign: 'left' }}>
                  <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                    <span style={{ font: font(500, 12.5), color: on ? C.text : o.rm ? C.purplePale : 'var(--om-body)' }}>{o.label}</span>
                    {o.sub && <span style={{ font: font(400, 11), color: C.muted }}>{o.sub}</span>}
                  </span>
                  <Icon name="check" size={14} color={C.purpleLt} style={{ opacity: on ? 1 : 0, marginTop: 1, flex: 'none' }} />
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

/** A 16px checkbox square (checked / partial / empty). */
export function CheckBox({ state }: { state: 'on' | 'some' | 'off' }) {
  const lit = state !== 'off';
  return (
    <span style={{ width: 16, height: 16, flex: 'none', boxSizing: 'border-box', borderRadius: 4, border: `1px solid ${lit ? C.purple : C.muted}`, background: lit ? C.purple : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
      {state === 'on' && <Icon name="check" size={11} />}
      {state === 'some' && <Icon name="minus" size={11} />}
    </span>
  );
}
